import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  CouponApi,
  CreateUpdateCouponRuleConfigRequest,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import { formSchema, CouponFormValues } from "./types";
import { formatDateForAPI } from "./utils";

interface CreateCouponFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function CreateCouponForm({
  onSuccess,
  onCancel,
}: CreateCouponFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuanceStartDateOpen, setIssuanceStartDateOpen] = useState(false);
  const [issuanceEndDateOpen, setIssuanceEndDateOpen] = useState(false);
  const [expiryDateOpen, setExpiryDateOpen] = useState(false);

  const form = useForm<CouponFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ruleName: "",
      invoiceValueFrom: "",
      invoiceValueTo: "",
      couponType: undefined,
      couponValue: "",
      issuanceStartDate: undefined,
      issuanceEndDate: undefined,
      expiryDate: undefined,
      usageLimit: undefined,
      redeemAmountFrom: "",
      redeemAmountTo: "",
    },
  });

  const onSubmit = async (data: CouponFormValues) => {
    setIsSubmitting(true);
    try {
      const usageLimitValue = data.usageLimit === "single" ? 1 : 2;
      const payload: CreateUpdateCouponRuleConfigRequest = {
        p_ruleName: data.ruleName,
        p_invoiceValueFrom: Number(data.invoiceValueFrom),
        p_invoiceValueTo: Number(data.invoiceValueTo),
        p_couponType: data.couponType === "flat" ? 1 : 2,
        p_couponValue: Number(data.couponValue),
        p_issuanceFrom: formatDateForAPI(data.issuanceStartDate),
        p_issuanceTo: formatDateForAPI(data.issuanceEndDate),
        p_Expiry: formatDateForAPI(data.expiryDate),
        p_ruleType: usageLimitValue,
        p_createdBy: 1,
        p_updatedBy: 0,
        p_UsageLimit: usageLimitValue,
        ...(data.redeemAmountFrom &&
          data.redeemAmountFrom.trim() !== "" && {
            p_RedeemAmountFrom: Number(data.redeemAmountFrom),
          }),
        ...(data.redeemAmountTo &&
          data.redeemAmountTo.trim() !== "" && {
            p_RedeemAmountTo: Number(data.redeemAmountTo),
          }),
      };

      // Log the payload to verify date formatting
      console.log("Coupon Rule Payload:", JSON.stringify(payload, null, 2));
      console.log("Issuance Start Date:", data.issuanceStartDate);
      console.log(
        "Formatted Issuance From:",
        formatDateForAPI(data.issuanceStartDate)
      );

      const response = await CouponApi.createUpdateCouponRuleConfig(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: response.message || "Coupon created successfully",
        });
        form.reset();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Error creating coupon:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to create coupon",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    setIssuanceStartDateOpen(false);
    setIssuanceEndDateOpen(false);
    setExpiryDateOpen(false);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Rule Name */}
          <FormField
            control={form.control}
            name="ruleName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rule Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter rule name" {...field} />
                </FormControl>
                <FormDescription>
                  Enter a unique name to identify this coupon rule
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Earn Criteria */}
          <div className="space-y-2">
            <FormLabel className="font-bold">Earn Criteria *</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="invoiceValueFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value From *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Min value" {...field} />
                    </FormControl>
                    <FormDescription>Minimum invoice amount</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="invoiceValueTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value To *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Max value" {...field} />
                    </FormControl>
                    <FormDescription>Maximum invoice amount</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Coupon Type and Usage Limit */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="couponType"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Coupon Type *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-row space-x-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="flat" id="flat" />
                        <label
                          htmlFor="flat"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Flat Discount
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="percentage" id="percentage" />
                        <label
                          htmlFor="percentage"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Percentage
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    Select whether the discount is a fixed amount or percentage
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="usageLimit"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Usage Limit *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-row space-x-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="single" id="single" />
                        <label
                          htmlFor="single"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Single Use
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="multiple" id="multiple" />
                        <label
                          htmlFor="multiple"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Multiple Use
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    Choose if the coupon can be used once or multiple times
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Coupon Value */}
          <FormField
            control={form.control}
            name="couponValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Coupon Value *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter coupon value"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent negative values
                      if (
                        value === "" ||
                        (!isNaN(Number(value)) && Number(value) >= 0)
                      ) {
                        field.onChange(value);
                      }
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Enter the discount value (amount or percentage based on coupon
                  type)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Issuance */}
          <div className="space-y-2">
            <FormLabel>Issuance *</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="issuanceStartDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start Date *</FormLabel>
                    <Popover
                      open={issuanceStartDateOpen}
                      onOpenChange={setIssuanceStartDateOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            if (date) {
                              setIssuanceStartDateOpen(false);
                            }
                          }}
                          disabled={(date) =>
                            date < new Date(new Date().setHours(0, 0, 0, 0))
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      When coupon issuance begins
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="issuanceEndDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>End Date *</FormLabel>
                    <Popover
                      open={issuanceEndDateOpen}
                      onOpenChange={setIssuanceEndDateOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            if (date) {
                              setIssuanceEndDateOpen(false);
                            }
                          }}
                          disabled={(date) => {
                            const startDate = form.getValues("issuanceStartDate");
                            const today = new Date(new Date().setHours(0, 0, 0, 0));
                            return (
                              date < today ||
                              (startDate && date < startDate)
                            );
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>When coupon issuance ends</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Redeem Criteria */}
          <div className="space-y-2">
            <FormLabel className="font-bold">Redeem Criteria</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="redeemAmountFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value From</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Min value" {...field} />
                    </FormControl>
                    <FormDescription>
                      Minimum invoice amount for redemption
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="redeemAmountTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value To</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Max value" {...field} />
                    </FormControl>
                    <FormDescription>
                      Maximum invoice amount for redemption
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Expiry */}
          <FormField
            control={form.control}
            name="expiryDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Expiry Date *</FormLabel>
                <Popover open={expiryDateOpen} onOpenChange={setExpiryDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        if (date) {
                          setExpiryDateOpen(false);
                        }
                      }}
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>
                  Date when issued coupons will expire
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit and Cancel Buttons */}
          <div className="flex justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
